import React, { useCallback, useMemo } from "react";
import {
  Users,
  Calendar,
  Clock,
  MapPin,
  MessageCircle,
  UserRoundCheck,
  Timer,
  ClipboardList,
  MoreHorizontal,
  Hand,
  Ban,
  Loader2
} from "lucide-react";
import { format } from "date-fns";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../../../../../components/eds/EDSDropdownMenu";
import { Enquiry, EnquiryStatus } from "../../../../../types/enquiries";
import { EDSBadge } from "../../../../../components/eds/EDSBadge";
import { useDiaryContext } from "../../../../../context/DiaryContext";
import { getBookingTypeById } from "../../../../../utils/singleBooking";
import { STATUS_CONFIG, STATUS_ACTIONS } from "../constants";
import { CopyableBookingRef } from "../../../../../components/Shared/CopyableBookingRef/CopyableBookingRef";

export interface EnquiryCardProps {
  enquiry: Enquiry;
  isRecentlyUpdated: boolean;
  onTake: () => void;
  onStatusChange: (status: EnquiryStatus) => void;
  onView: () => void;
}

export const EnquiryCard: React.FC<EnquiryCardProps> = ({
  enquiry,
  isRecentlyUpdated,
  onTake,
  onStatusChange,
  onView
}) => {
  const { venueConfig } = useDiaryContext();

  const bookingTypeColour = useMemo(() => {
    if (!enquiry.booking_type) return undefined;
    const venueBookingType = getBookingTypeById({
      bookingTypeId: enquiry.booking_type.id,
      venueConfig
    });
    return venueBookingType?.color ?? enquiry.booking_type.color;
  }, [enquiry.booking_type?.id, enquiry.booking_type?.color, venueConfig]);

  const statusInfo = STATUS_CONFIG[enquiry.status];
  const eventDate = enquiry.date ? new Date(enquiry.date) : null;
  const createdDate = enquiry.created ? new Date(enquiry.created) : null;

  const waitingHours = createdDate
    ? (Date.now() - createdDate.getTime()) / (1000 * 60 * 60)
    : null;
  const isWaitingTwoHours =
    waitingHours !== null && waitingHours >= 2 && enquiry.status === "new";

  const hasMention =
    enquiry.comments?.some((comment) => comment.text?.includes("@")) ?? false;

  const statusIconContent =
    enquiry.status === "in_progress" ? (
      <Loader2 size={14} />
    ) : (
      statusInfo.initial
    );

  const customerName =
    [enquiry.customer?.first_name, enquiry.customer?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || "Guest";

  const handleCardClick = useCallback(() => {
    // This will be called directly by the main button
    onView();
  }, [onView]);

  const handleTakeClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onTake();
    },
    [onTake]
  );

  const handleStatusChange = useCallback(
    (status: EnquiryStatus) => {
      onStatusChange(status);
    },
    [onStatusChange]
  );

  return (
    <div
      className={`relative ${isRecentlyUpdated ? "animate-pulse bg-green-500/10" : ""}`}
    >
      {/* Status icon - positioned consistently for all statuses */}
      {enquiry.status === "new" ? (
        <button
          className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center ${statusInfo.bgClass} ${statusInfo.textClass} hover:opacity-80 transition-opacity`}
          onClick={handleTakeClick}
          aria-label="Take enquiry"
          title="Take enquiry"
        >
          <Hand size={16} />
        </button>
      ) : (
        <div
          className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${statusInfo.bgClass} ${statusInfo.textClass}`}
        >
          {enquiry.status === "cancelled" ? (
            <Ban size={14} />
          ) : (
            statusIconContent
          )}
        </div>
      )}

      <div className="absolute right-3 top-3 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2 rounded-md hover:bg-accent transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="More options"
            >
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {STATUS_ACTIONS.map((action) => (
              <DropdownMenuItem
                key={action.value}
                onSelect={() => handleStatusChange(action.value)}
              >
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Main clickable button covering the content area */}
      <button
        className="w-full flex items-center gap-3 p-3 pl-14 pr-16 border-b border-border bg-card hover:bg-accent/50 cursor-pointer transition-colors text-left border-0 outline-none"
        onClick={handleCardClick}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground truncate">
              {customerName}
            </span>

            <div className="flex items-center gap-1">
              {(enquiry.unread_messages ?? 0) > 0 && (
                <MessageCircle
                  className="text-blue-500"
                  size={14}
                  aria-label={`${enquiry.unread_messages} unread messages`}
                />
              )}
              {enquiry.follow_up && (
                <UserRoundCheck
                  className="text-amber-500"
                  size={14}
                  aria-label="Follow up required"
                />
              )}
              {isWaitingTwoHours && (
                <Timer
                  className="text-red-500"
                  size={14}
                  aria-label="Waiting 2+ hours"
                />
              )}
              {hasMention && (
                <ClipboardList
                  className="text-purple-500"
                  size={14}
                  aria-label="Mentioned in notes"
                />
              )}
            </div>

            {enquiry.booking_type && (
              <EDSBadge
                className="DiarySidebarListItem__detailsType"
                variant="default"
                style={
                  {
                    "--booking-type-color":
                      bookingTypeColour ?? "hsl(var(--primary))",
                    "--booking-type-foreground": bookingTypeColour
                      ? "white"
                      : "hsl(var(--primary-foreground))",
                    maxWidth: "140px"
                  } as React.CSSProperties
                }
              >
                <span
                  className="DiarySidebarListItem_detailsTypeText truncate max-w-[120px] inline-block"
                  title={enquiry.booking_type.name}
                >
                  {enquiry.booking_type.name}
                </span>
              </EDSBadge>
            )}
          </div>

          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
            {enquiry.num_people && (
              <span className="flex items-center gap-1">
                <Users size={14} />
                {enquiry.num_people}
              </span>
            )}

            {eventDate && (
              <>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {format(eventDate, "EEE d MMM")}
                </span>
                {enquiry.time && (
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {enquiry.time}
                  </span>
                )}
              </>
            )}

            {enquiry.venue?.title && (
              <span className="flex items-center gap-1">
                <MapPin size={14} />
                {enquiry.venue.title}
              </span>
            )}

            <span className="flex items-center gap-1 text-xs text-muted-foreground/70">
              <CopyableBookingRef
                reference={enquiry.reference}
                className="text-xs text-muted-foreground/70 font-normal"
              />
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};
